import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';
import { useResponsive } from 'src/hooks/use-responsive';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import fr from 'date-fns/locale/fr';
import en from 'date-fns/locale/en-US';
import {
  addDays,
  parseISO,
  subDays,
  startOfMonth,
  startOfYear,
  subMonths,
  endOfMonth,
  differenceInDays,
  subYears,
  isValid,
  startOfDay,
  startOfWeek
} from 'date-fns';
import { useEffect, useState, useRef } from 'react';
import { DateRangePicker, createStaticRanges, defaultInputRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { useLocales } from 'src/locales';
import "./date-picker-custom.css";
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { enUS } from 'date-fns/locale';
import { getLast12WeeksDateRange } from 'src/utils/format-time';

const ALIGN_TIMESTEPS = new Set(['year_week', 'year_month']);

const alignDateToGranularity = (date: Date, timestep?: string): Date => {
  if (!timestep || !ALIGN_TIMESTEPS.has(timestep)) return date;

  if (timestep === 'year_month') return startOfMonth(date);

  return startOfWeek(date, { weekStartsOn: 1 });
};

export default function CustomDateRangePicker({
  variant = 'input',
  presets,
  onChangeStartDate,
  onChangeEndDate,
  onChangeCompareStartDate,
  onChangeCompareEndDate,
  error,
  enableComparison,
}: any) {

  const mdUp = useResponsive('up', 'md');
  const filterStore = useSelector((state: any) => state.filters);
  const theme: any = useTheme();
  const { currentLang, t } = useLocales();
  const workspace = useCurrentWorkspace();

  const fallbackDate = startOfDay(new Date());

  const hasAppliedInitialComparisonAlignment = useRef(false);

  const [state, setState] = useState(() => ({
    selection1: {
      startDate: isValid(parseISO(filterStore?.start)) ? parseISO(filterStore?.start) : fallbackDate,
      endDate: isValid(parseISO(filterStore?.end)) ? parseISO(filterStore?.end) : fallbackDate,
      key: 'selection1',
    },
    selection2: {
      startDate: isValid(parseISO(filterStore?.compareStart)) ? parseISO(filterStore?.compareStart) : fallbackDate,
      endDate: isValid(parseISO(filterStore?.compareEnd)) ? parseISO(filterStore?.compareEnd) : fallbackDate,
      key: 'selection2',
    },
  }));

  // Sync redux store
  useEffect(() => {
    setState({
      selection1: {
        startDate: parseISO(filterStore?.start),
        endDate: parseISO(filterStore?.end),
        key: 'selection1',
      },
      selection2: {
        startDate: parseISO(filterStore?.compareStart),
        endDate: parseISO(filterStore?.compareEnd),
        key: 'selection2',
      },
    });
  }, [filterStore]);

  // INITIAL comparison alignment ONLY ONCE
  useEffect(() => {
    if (!enableComparison) {
      hasAppliedInitialComparisonAlignment.current = false;
      return;
    }

    if (hasAppliedInitialComparisonAlignment.current) return;
    hasAppliedInitialComparisonAlignment.current = true;

    const { startDate, endDate } = state.selection1;
    const timestep = filterStore?.timestep;

    const diffDays = differenceInDays(endDate, startDate) + 1;

    const previousEnd = subDays(startDate, 1);
    const previousStart = subDays(previousEnd, diffDays - 1);

    setState(prev => ({
      ...prev,
      selection2: {
        startDate: previousStart,
        endDate: previousEnd,
        key: 'selection2',
      },
    }));
  }, [enableComparison]);

  // callbacks
  useEffect(() => {
    onChangeStartDate?.(state.selection1.startDate);
    onChangeEndDate?.(state.selection1.endDate);
  }, [state.selection1]);

  useEffect(() => {
    onChangeCompareStartDate?.(state.selection2.startDate);
    onChangeCompareEndDate?.(state.selection2.endDate);
  }, [state.selection2]);

  // theme
  useEffect(() => {
    if (theme.palette.mode === "dark") {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }, [theme.palette.mode]);


  const computePreviousPeriod = (startDate: Date, endDate: Date) => {
    const diffDays = differenceInDays(endDate, startDate) + 1;

    const previousEnd = subDays(startDate, 1);
    const previousStart = subDays(previousEnd, diffDays - 1);

    return {
      startDate: previousStart,
      endDate: previousEnd,
      key: 'selection2',
    };
  };

  const computeLastYear = (startDate: Date, endDate: Date) => {
    const diffDays = differenceInDays(endDate, startDate) + 1;

    const previousEnd = subYears(endDate, 1);
    const previousStart = subDays(previousEnd, diffDays - 1);

    return {
      startDate: previousStart,
      endDate: previousEnd,
      key: 'selection2',
    };
  };

  const handleRangeChange = (item: any) => {
    if (item.selection1) {
      const newStart = item.selection1.startDate;
      const newEnd = item.selection1.endDate;

      const lastYearRange = customStaticRanges.find(
        (r) => r.label === t('dashboard.filters.last_year')
      );

      const previousPeriodRange = customStaticRanges.find(
        (r) => r.label === t('dashboard.filters.previous_period')
      );

      const isSameRange = (a: any, b: any) =>
        a?.startDate?.getTime() === b?.startDate?.getTime() &&
        a?.endDate?.getTime() === b?.endDate?.getTime();

      // 🧠 CASE 1: LAST YEAR
      const isLastYear =
        lastYearRange &&
        isSameRange(
          { startDate: newStart, endDate: newEnd },
          lastYearRange.range()
        );

      if (isLastYear) {
        setState((prev) => ({
          ...prev,
          selection2: computeLastYear(
            prev.selection1.startDate,
            prev.selection1.endDate
          ),
        }));
        return;
      }

      // 🧠 CASE 2: PREVIOUS PERIOD
      const isPreviousPeriod =
        previousPeriodRange &&
        isSameRange(
          { startDate: newStart, endDate: newEnd },
          previousPeriodRange.range()
        );

      if (isPreviousPeriod) {
        setState((prev) => ({
          ...prev,
          selection2: computePreviousPeriod(
            prev.selection1.startDate,
            prev.selection1.endDate
          ),
        }));
        return;
      }

      // 🧠 DEFAULT: normal selection
      setState((prev) => ({
        ...prev,
        selection1: { ...item.selection1, key: 'selection1' },
      }));

      return;
    }

    if (item.selection2) {
      setState((prev) => ({
        ...prev,
        selection2: { ...item.selection2, key: 'selection2' },
      }));
    }
  };

  const customStaticRanges = createStaticRanges([
    {
      label: t('dashboard.filters.this_month'),
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_month'),
      range: () => ({
        startDate: startOfMonth(subMonths(new Date(), 1)),
        endDate: endOfMonth(subMonths(new Date(), 1)),
      }),
    },
    {
      label: t('dashboard.filters.last_7_days'),
      range: () => ({
        startDate: subDays(new Date(), 7),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_14_days'),
      range: () => ({
        startDate: subDays(new Date(), 14),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_30_days'),
      range: () => ({
        startDate: subDays(new Date(), 30),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_90_days'),
      range: () => ({
        startDate: subDays(new Date(), 90),
        endDate: subDays(new Date(), 1),
      }),
    },
    {
      label: t('dashboard.filters.last_12_weeks'),
      range: () => {
        const { start, end } = getLast12WeeksDateRange();
        return {
          startDate: new Date(start),
          endDate: new Date(end),
        };
      },
    },
    {
      label: t('dashboard.filters.this_year'),
      range: () => ({
        startDate: startOfYear(new Date()),
        endDate: subDays(new Date(), 1),
      }),
    },
    ...(enableComparison ? [
      {
        label: '‎ ‎ ‎ ‎ ',
        range: () => {

          return {

          };
        },
      },
      {
        label: t('dashboard.filters.previous_period'),
        range: () =>
          computePreviousPeriod(
            state.selection1.startDate,
            state.selection1.endDate
          ),
      },
      {
        label: t('dashboard.filters.last_year'),
        range: () =>
          computeLastYear(
            state.selection1.startDate,
            state.selection1.endDate
          ),
      }
    ] : []),
  ]);

  const customInputRanges = defaultInputRanges;

  const isCalendarView = variant === 'calendar';

  const customEnglishLocale: any = {
    ...enUS,
    options: {
      ...enUS.options,
      weekStartsOn: 1,
    },
  };

  return (
    <>
      <Stack
        justifyContent="center"
        spacing={isCalendarView ? (presets ? 2 : 3) : 2}
        direction={isCalendarView && mdUp ? 'row' : 'column'}
        sx={{ pt: 1 }}
      >
        <Paper variant="outlined" sx={{
          borderRadius: 2,
          borderColor: 'divider',
          borderStyle: 'dashed',
        }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={currentLang?.value === "en" ? en : fr}
          >
            <DateRangePicker
              onChange={handleRangeChange}
              ranges={enableComparison ? [state.selection1, state.selection2] : [state.selection1]}
              minDate={
                isValid(new Date(filterStore?.minDatePicker))
                  ? new Date(filterStore.minDatePicker)
                  : subYears(new Date(), 1)
              }
              
              maxDate={workspace?.url === "client" ? new Date(2024, 10, 1) : addDays(new Date(), -1)}
              locale={currentLang.value === 'en' ? customEnglishLocale : fr}
              direction="vertical"
              scroll={{ enabled: true }}
              editableDateInputs
              staticRanges={customStaticRanges}
              inputRanges={customInputRanges}
              dateDisplayFormat={currentLang?.value === "en" ? "MM/dd/yyyy" : 'dd/MM/yyyy'}
            />
          </LocalizationProvider>
        </Paper>
      </Stack>

      {error && (
        <FormHelperText error sx={{ px: 2 }}>
          {t('dashboard.filters.end_date_must_be_later_than_start_date')}
        </FormHelperText>
      )}
    </>
  );
}